/**
 * CareerTeamPanel — ★ Fase 33: vista Direttori/Admin del team ES per livello.
 * Sofia/Mattia possono cambiare livello (Setter/Junior/Senior) e segnare il
 * "traguardo" (fisso maggiorato / % maggiorata) per ogni agente, con audit.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  ChevronLeft, ChevronRight, Users, Wallet, TrendingUp, Trophy, Percent,
} from 'lucide-react';
import { fmtUSD, LEVEL_META, monthLabel } from './careerUtils';

const LEVELS = ['senior', 'junior', 'setter'];

function TotalChip({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-lg border bg-card px-3 py-2 min-w-[120px]" data-testid={testId}>
      <div className="text-[11px] text-muted-foreground flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export function CareerTeamPanel() {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [month, setMonth] = useState(now.getUTCMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/career/team', { params: { year, month } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const updateAgent = async (agentId, payload) => {
    setSavingId(agentId);
    try {
      await api.post(`/career/level/${agentId}`, payload);
      toast.success(t('career.levelUpdated', 'Nivel actualizado'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('career.updateError', 'Error al actualizar'));
    } finally {
      setSavingId(null);
    }
  };

  const grouped = useMemo(() => {
    const g = { senior: [], junior: [], setter: [] };
    (data?.agents || []).forEach((a) => {
      (g[a.career_level] || g.setter).push(a);
    });
    return g;
  }, [data]);

  const mLabel = monthLabel(i18n.language, year, month);

  return (
    <Card className="border-emerald-100" data-testid="career-team-panel">
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-serif text-xl text-[hsl(var(--primary))] flex items-center gap-2" data-testid="career-team-title">
              <Users className="w-5 h-5" />
              {t('career.teamTitle', 'Carrera del equipo')}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('career.teamSubtitle', 'Niveles, objetivos y ganancias del equipo — divididos por nivel (USD)')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => changeMonth(-1)} data-testid="career-team-month-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium min-w-[130px] text-center" data-testid="career-team-month-label">{mLabel}</span>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => changeMonth(1)} data-testid="career-team-month-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !data ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="career-team-error">
            {t('career.loadError', 'No se pudieron cargar los datos. Inténtalo de nuevo.')}
            <div className="mt-2">
              <Button variant="outline" size="sm" onClick={load}>{t('common.retry', 'Reintentar')}</Button>
            </div>
          </div>
        ) : (
          <>
            {/* Totali mese */}
            <div className="flex gap-2 flex-wrap">
              <TotalChip icon={TrendingUp} label={t('career.sold', 'Vendido')} value={fmtUSD(data.totals?.sold)} testId="career-total-sold" />
              <TotalChip icon={Wallet} label={t('career.collected', 'Cobrado')} value={fmtUSD(data.totals?.collected)} testId="career-total-collected" />
              <TotalChip icon={Percent} label={t('career.teamPayroll', 'Nómina estimada')} value={fmtUSD(data.totals?.payroll)} testId="career-total-payroll" />
              <TotalChip icon={Users} label={t('career.leadsAssigned', 'Leads asignados')} value={`${data.totals?.leads_assigned ?? 0} · ${data.totals?.shows ?? 0} ${t('career.shows', 'shows')}`} testId="career-total-leads" />
            </div>

            {/* Sezioni per livello */}
            {LEVELS.map((lvl) => {
              const meta = LEVEL_META[lvl];
              const LevelIcon = meta.icon;
              const rows = grouped[lvl] || [];
              return (
                <div key={lvl} className="space-y-2" data-testid={`career-level-section-${lvl}`}>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`${meta.badge} gap-1`}>
                      <LevelIcon className="w-3 h-3" />
                      {t(`career.levelsPlural.${lvl}`, lvl)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">({rows.length})</span>
                  </div>
                  {rows.length === 0 ? (
                    <div className="text-xs text-muted-foreground pl-1 pb-1">
                      {t('career.noAgents', 'No hay agentes en este nivel.')}
                    </div>
                  ) : (
                    <div className="rounded-lg border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">{t('career.agent', 'Agente')}</TableHead>
                            <TableHead className="text-xs text-right">
                              {lvl === 'setter'
                                ? t('career.showPctShort', '% Shows')
                                : t('career.soldCollected', 'Vendido / Cobrado')}
                            </TableHead>
                            <TableHead className="text-xs text-right">{t('career.estEarnings', 'Ganancia est.')}</TableHead>
                            <TableHead className="text-xs">{t('career.level', 'Nivel')}</TableHead>
                            <TableHead className="text-xs">
                              <span className="inline-flex items-center gap-1">
                                <Trophy className="w-3 h-3" />{t('career.milestoneShort', 'Meta')}
                              </span>
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {rows.map((a) => (
                            <TableRow key={a.agent_id} data-testid={`career-agent-row-${a.agent_id}`}>
                              <TableCell className="py-2">
                                <div className="text-sm font-medium">{a.agent_name}</div>
                                {(a.warnings || []).length > 0 && (
                                  <div className="text-[10px] text-amber-700">
                                    {a.warnings.includes('yellow_card') && t('career.yellowCardShort', 'Tarjeta amarilla')}
                                    {a.warnings.includes('termination_risk') && t('career.terminationShort', 'Riesgo de rescisión')}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="py-2 text-right text-sm tabular-nums">
                                {lvl === 'setter' ? (
                                  <>
                                    <span className="font-semibold">{a.show_pct}%</span>
                                    <span className="text-xs text-muted-foreground ml-1">
                                      ({a.shows}/{a.leads_assigned})
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {fmtUSD(a.sold)}
                                    <span className="text-xs text-muted-foreground"> / {fmtUSD(a.collected)}</span>
                                  </>
                                )}
                              </TableCell>
                              <TableCell className="py-2 text-right text-sm font-semibold tabular-nums text-emerald-800">
                                {fmtUSD(a.earnings?.total)}
                              </TableCell>
                              <TableCell className="py-2">
                                <Select
                                  value={a.career_level}
                                  onValueChange={(v) => updateAgent(a.agent_id, { career_level: v })}
                                  disabled={savingId === a.agent_id}
                                >
                                  <SelectTrigger className="h-8 w-[120px] text-xs" data-testid={`career-level-select-${a.agent_id}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="setter">{t('career.levels.setter', 'Setter')}</SelectItem>
                                    <SelectItem value="junior">{t('career.levels.junior', 'Junior')}</SelectItem>
                                    <SelectItem value="senior">{t('career.levels.senior', 'Senior')}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="py-2">
                                <Switch
                                  checked={!!a.career_milestone}
                                  onCheckedChange={(v) => updateAgent(a.agent_id, { career_milestone: v })}
                                  disabled={savingId === a.agent_id}
                                  data-testid={`career-milestone-switch-${a.agent_id}`}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}

            <p className="text-[11px] text-muted-foreground">
              {t('career.milestoneHint', '"Meta" = objetivo alcanzado según el contrato: Setter → fijo $600 · Junior → fijo $1.200 + 5% · Senior → override 3% retro.')}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
